/* ─────────────────────────────────────────────────────────────
   The PQ test workspace's logic: a frozen piece of evidence, the
   operating criteria it is checked against, and the record that comes
   out. All of it runs in the browser and nothing leaves it.

   An imported file is the only outside input on the demo, so it is treated
   as untrusted: the size is capped, every field is checked, and its values
   only ever reach the page as text.
   ───────────────────────────────────────────────────────────── */

import { PHASES } from './model';

export const MAX_IMPORT_BYTES = 200 * 1024;

export const DEFAULT_CRITERIA = {
    voltage: '5',
    frequency: '0.5',
    thdV: '5',
    thdI: '20',
    negativeSequence: '2',
    powerFactor: '0.95',
    demandCurrent: '',
    shortCircuit: ''
};

export const CRITERIA_FIELDS = [
    { key: 'voltage', label: 'RMS voltage tolerance', unit: '± % nominal', min: 0.1, max: 20, step: 0.1 },
    { key: 'frequency', label: 'Frequency tolerance', unit: '± Hz nominal', min: 0.01, max: 5, step: 0.01 },
    { key: 'thdV', label: 'Voltage THD ceiling', unit: '%', min: 0.1, max: 50, step: 0.1 },
    { key: 'thdI', label: 'Current THD advisory', unit: '%', min: 0.1, max: 100, step: 0.1 },
    { key: 'negativeSequence', label: 'Negative-sequence V₂ / V₁', unit: '% maximum', min: 0.01, max: 20, step: 0.01 },
    { key: 'powerFactor', label: 'Minimum true power factor', unit: 'magnitude, 0 to 1', min: 0.01, max: 1, step: 0.01 }
];

/* The first thing wrong with the criteria, in words, or null. Said out loud
   because a disabled Run button on its own explains nothing. */
export const criteriaError = criteria => {
    for (const field of CRITERIA_FIELDS) {
        const raw = String(criteria[field.key]).trim();
        const value = Number(raw);

        if (raw === '' || !Number.isFinite(value))
            return `${field.label} needs a value before the assessment can run.`;

        if (value < field.min || value > field.max)
            return `${field.label} must be between ${field.min} and ${field.max}.`;
    }

    for (const [key, label] of [['demandCurrent', 'Maximum demand current IL'], ['shortCircuit', 'Short-circuit current ISC']]) {
        const raw = String(criteria[key]).trim();

        if (raw !== '' && !(Number(raw) > 0))
            return `${label} must be a positive number, or left empty.`;
    }

    return null;
};

/* ── Evidence ───────────────────────────────────────────────── */

export const evidenceFromDemo = (ac, ms) => ({
    origin: 'SIMULATED',
    source: 'demo',
    meterId: ac.meterTag,
    meterLabel: ac.meter.label,
    path: ac.path,
    timestamp: ms,
    nominalLl: ac.profile.ll,
    nominalHz: ac.profile.hz,
    frequency: ac.frequency,
    quality: 'valid',
    totalKw: ac.totalKw,
    phases: ac.phases.map(p => ({
        name: p.name,
        v: p.vRms,
        i: p.iRms,
        i1: p.i1,
        pKw: p.pKw,
        sKva: p.sKva,
        thdV: p.thdV,
        thdI: p.thdI,
        vAngle: p.vAngle
    }))
});

const CSV_COLUMNS = [
    'data_mode', 'timestamp_utc', 'meter_id', 'nominal_v_ll', 'nominal_hz', 'frequency_hz',
    'phase', 'v_rms', 'i_rms', 'p_kw', 's_kva', 'thd_v_pct', 'thd_i_pct', 'v_angle_deg', 'quality'
];

/* Splits one CSV line, honoring quotes. Enough for the schema this page
   publishes, and strict about everything that follows it. */
const splitLine = line => {
    const cells = [];
    let cell = '';
    let quoted = false;

    for (let i = 0; i < line.length; i += 1) {
        const char = line[i];

        if (quoted) {
            if (char === '"' && line[i + 1] === '"') {
                cell += '"';
                i += 1;
            } else if (char === '"') {
                quoted = false;
            } else {
                cell += char;
            }
        } else if (char === '"') {
            quoted = true;
        } else if (char === ',') {
            cells.push(cell);
            cell = '';
        } else {
            cell += char;
        }
    }

    cells.push(cell);
    return cells.map(value => value.trim());
};

const number = (row, key, phase) => {
    const value = Number(row[key]);

    if (row[key] === '' || !Number.isFinite(value))
        throw new Error(`${phase}: ${key} has to be a number.`);

    return value;
};

/* Turns an imported file into evidence, or throws with a reason a person can
   act on. Checks the schema, phase coverage and power consistency. It cannot
   check calibration or method, and the record says so. */
export const evidenceFromCsv = text => {
    const lines = String(text).split(/\r?\n/).map(line => line.trim()).filter(Boolean);

    if (lines.length < 2)
        throw new Error('The file needs a header row and one row for each phase.');

    const header = splitLine(lines[0]).map(name => name.toLowerCase());
    const missing = CSV_COLUMNS.filter(column => !header.includes(column));

    if (missing.length)
        throw new Error(`Missing columns: ${missing.join(', ')}.`);

    const rows = lines.slice(1).map(line => {
        const cells = splitLine(line);
        return Object.fromEntries(header.map((name, i) => [name, cells[i] === undefined ? '' : cells[i]]));
    });

    const seen = rows.map(row => row.phase.toUpperCase());
    const absent = PHASES.filter(name => !seen.includes(name));

    if (absent.length || rows.length !== PHASES.length)
        throw new Error(`The file needs exactly one row each for L1, L2 and L3 at one timestamp${absent.length ? `; missing ${absent.join(', ')}` : ''}.`);

    if (new Set(rows.map(row => row.timestamp_utc)).size !== 1)
        throw new Error('All three phases have to share one timestamp.');

    if (new Set(rows.map(row => row.meter_id)).size !== 1)
        throw new Error('All three phases have to come from one meter.');

    const mode = rows[0].data_mode.toUpperCase();

    if (mode !== 'MEASURED' && mode !== 'SIMULATED')
        throw new Error('data_mode has to be MEASURED or SIMULATED.');

    const timestamp = Date.parse(rows[0].timestamp_utc);

    if (Number.isNaN(timestamp))
        throw new Error('timestamp_utc is not a date this page can read.');

    const phases = PHASES.map(name => {
        const row = rows.find(r => r.phase.toUpperCase() === name);
        const v = number(row, 'v_rms', name);
        const i = number(row, 'i_rms', name);
        const pKw = number(row, 'p_kw', name);
        const sKva = number(row, 's_kva', name);
        const thdI = number(row, 'thd_i_pct', name);

        if (v <= 0 || i < 0 || sKva < 0)
            throw new Error(`${name}: voltage, current and apparent power cannot be negative.`);

        const computed = (v * i) / 1000;

        if (sKva > 0 && Math.abs(computed - sKva) / sKva > 0.05)
            throw new Error(`${name}: s_kva does not agree with v_rms × i_rms.`);

        if (Math.abs(pKw) > sKva * 1.02)
            throw new Error(`${name}: active power cannot exceed apparent power.`);

        return {
            name,
            v,
            i,
            i1: i / Math.sqrt(1 + (thdI / 100) ** 2),
            pKw,
            sKva,
            thdV: number(row, 'thd_v_pct', name),
            thdI,
            vAngle: number(row, 'v_angle_deg', name)
        };
    });

    return {
        origin: mode,
        source: 'import',
        meterId: rows[0].meter_id || 'Imported meter',
        meterLabel: 'Imported snapshot',
        path: null,
        timestamp,
        nominalLl: number(rows[0], 'nominal_v_ll', 'L1'),
        nominalHz: number(rows[0], 'nominal_hz', 'L1'),
        frequency: number(rows[0], 'frequency_hz', 'L1'),
        quality: rows.every(row => row.quality.toLowerCase() === 'valid') ? 'valid' : 'flagged',
        totalKw: phases.reduce((total, phase) => total + phase.pKw, 0),
        phases
    };
};

/* The template a visitor can download: the schema, filled in from the demo
   meter and marked SIMULATED so it can never pass as a real reading. */
export const templateCsv = evidence => {
    const rows = evidence.phases.map(phase => [
        'SIMULATED',
        new Date(evidence.timestamp).toISOString(),
        evidence.meterId,
        evidence.nominalLl,
        evidence.nominalHz,
        evidence.frequency,
        phase.name,
        phase.v.toFixed(2),
        phase.i.toFixed(2),
        phase.pKw.toFixed(2),
        phase.sKva.toFixed(2),
        phase.thdV.toFixed(2),
        (phase.thdI === null ? 0 : phase.thdI).toFixed(2),
        phase.vAngle.toFixed(1),
        'valid'
    ]);

    return [CSV_COLUMNS, ...rows].map(row => row.join(',')).join('\n');
};

/* ── The checks ─────────────────────────────────────────────── */

const unbalance = phases => {
    const DEG = Math.PI / 180;
    const fundamental = phases.map(phase => {
        const v1 = phase.v / Math.sqrt(1 + (phase.thdV / 100) ** 2);
        return [v1 * Math.cos(phase.vAngle * DEG), v1 * Math.sin(phase.vAngle * DEG)];
    });
    const rotate = ([x, y], deg) => {
        const [c, s] = [Math.cos(deg * DEG), Math.sin(deg * DEG)];
        return [x * c - y * s, x * s + y * c];
    };
    const size = (a, b) => {
        const parts = [fundamental[0], rotate(fundamental[1], a), rotate(fundamental[2], b)];
        return Math.hypot(
            parts.reduce((t, p) => t + p[0], 0) / 3,
            parts.reduce((t, p) => t + p[1], 0) / 3
        );
    };

    const positive = size(120, 240);

    return positive > 0 ? (size(240, 120) / positive) * 100 : null;
};

const rotation = phases => {
    if (phases.some(phase => !(phase.v > 0))) return 'Unknown';

    const wrap = deg => ((deg % 360) + 540) % 360 - 180;
    const second = wrap(phases[1].vAngle - phases[0].vAngle);
    const third = wrap(phases[2].vAngle - phases[0].vAngle);

    if (second < 0 && third > 0) return 'ABC';
    if (second > 0 && third < 0) return 'ACB';
    return 'Unknown';
};

const within = passed => (passed ? 'within' : 'advisory');

/* Run the ten checks: eight operating, two readiness. Both the evidence and
   the limits are copied into the record, so changing either on the page
   afterwards cannot rewrite a saved result. */
export const assess = (evidence, criteria, id, ranAt) => {
    const limits = Object.fromEntries(
        Object.entries(criteria).map(([key, value]) => [key, value === '' ? null : Number(value)])
    );
    const { phases } = evidence;
    const nominalLn = evidence.nominalLl / Math.sqrt(3);

    const deviations = phases.map(phase => ({
        name: phase.name,
        pct: (Math.abs(phase.v - nominalLn) / nominalLn) * 100
    }));
    const worstV = deviations.reduce((worst, item) => (item.pct > worst.pct ? item : worst));
    const freqOff = Math.abs(evidence.frequency - evidence.nominalHz);
    const maxThdV = Math.max(...phases.map(p => p.thdV));
    const loaded = phases.filter(p => p.i >= 1 && p.thdI !== null);
    const maxThdI = loaded.length ? Math.max(...loaded.map(p => p.thdI)) : null;
    const negSeq = unbalance(phases);
    const totalS = phases.reduce((t, p) => t + p.sKva, 0);
    const pf = totalS > 0 ? Math.abs(evidence.totalKw) / totalS : null;
    const order = rotation(phases);

    const harmonicA = loaded.length
        ? Math.max(...loaded.map(p => p.i1 * (p.thdI / 100)))
        : null;
    const tdd = limits.demandCurrent && harmonicA !== null
        ? (harmonicA / limits.demandCurrent) * 100
        : null;

    const checks = [
        {
            name: 'Record quality & phase coverage',
            note: evidence.source === 'demo'
                ? 'Valid simulated calculation; no physical meter is connected.'
                : 'Schema, phase coverage and power consistency checked on import. Source calibration and methods remain unverified.',
            measured: `3/3 phase records · ${evidence.quality}`,
            criterion: 'Three complete phases with valid source quality',
            outcome: within(evidence.quality === 'valid')
        },
        {
            name: 'Phase-to-neutral RMS voltage',
            note: 'Each phase checked independently against the selected nominal voltage.',
            measured: `${worstV.name}: ${worstV.pct.toFixed(2)}% maximum deviation`,
            criterion: `Within ±${limits.voltage}% of ${nominalLn.toFixed(2)} V`,
            outcome: within(worstV.pct <= limits.voltage)
        },
        {
            name: 'Power frequency',
            note: 'Single captured measurement; not a long-term frequency assessment.',
            measured: `${evidence.frequency.toFixed(3)} Hz`,
            criterion: `${evidence.nominalHz} ± ${limits.frequency} Hz`,
            outcome: within(freqOff <= limits.frequency)
        },
        {
            name: 'Voltage harmonic distortion',
            note: 'Illustrative ceiling; orders 2–50 for demo records.',
            measured: `${maxThdV.toFixed(2)}% worst phase`,
            criterion: `THD V ≤ ${limits.thdV}%`,
            outcome: within(maxThdV <= limits.thdV)
        },
        {
            name: 'Current harmonic distortion',
            note: 'Based on present fundamental current. This is not IEEE 519 TDD. Ratios below 1 A are suppressed.',
            measured: maxThdI === null ? 'No loaded phase' : `${maxThdI.toFixed(2)}% worst loaded phase`,
            criterion: `THD I ≤ ${limits.thdI}% advisory`,
            outcome: maxThdI === null ? 'none' : within(maxThdI <= limits.thdI)
        },
        {
            name: 'Voltage negative-sequence unbalance',
            note: 'Negative-to-positive fundamental sequence magnitude; not maximum phase-magnitude deviation.',
            measured: negSeq === null ? 'No voltage at this meter' : `${negSeq.toFixed(3)}%`,
            criterion: `V₂ / V₁ ≤ ${limits.negativeSequence}%`,
            outcome: negSeq === null ? 'none' : within(negSeq <= limits.negativeSequence)
        },
        {
            name: 'Aggregate true power factor',
            note: 'Magnitude of active power over the sum of phase apparent powers. Direction is kept separately.',
            measured: pf === null ? 'Undefined at zero current' : `${pf.toFixed(4)} · ${evidence.totalKw >= 0 ? 'import' : 'export'}`,
            criterion: `|True PF| ≥ ${limits.powerFactor}`,
            outcome: pf === null ? 'none' : within(pf >= limits.powerFactor)
        },
        {
            name: 'Phase rotation',
            note: 'Declared or measured phase order must agree with installation phase mapping.',
            measured: order,
            criterion: 'L1 → L2 → L3 (ABC)',
            outcome: within(order === 'ABC')
        },
        {
            name: 'TDD & IEEE 519 assessment',
            note: 'A spot reading cannot establish IEEE 519 compliance, so this stays unassessed whatever the number says.',
            measured: tdd === null
                ? 'IL reference not configured'
                : `TDD ${tdd.toFixed(2)}% at IL = ${limits.demandCurrent} A`,
            criterion: 'Confirmed PCC, IL, ISC / IL, method and statistical observation',
            outcome: 'none'
        },
        {
            name: 'Instrument method & calibration',
            note: 'IEC 61000-4-30 describes measurement methods. This dashboard cannot certify the instrument.',
            measured: 'No verified instrument record',
            criterion: 'Documented method class, edition, CT/VT chain and calibration',
            outcome: 'none'
        }
    ];

    const counts = {
        within: checks.filter(c => c.outcome === 'within').length,
        advisory: checks.filter(c => c.outcome === 'advisory').length,
        none: checks.filter(c => c.outcome === 'none').length
    };

    return {
        id,
        ranAt,
        evidence,
        criteria: limits,
        checks,
        counts,
        verdict: counts.advisory > 0 ? 'Configured advisory exceeded' : 'Spot checks complete, assessment partial',
        tone: counts.advisory > 0 ? 'warn' : 'info',
        short: counts.advisory > 0
            ? `${counts.advisory} ${counts.advisory === 1 ? 'advisory' : 'advisories'}`
            : 'Partial assessment'
    };
};

const OUTCOME_LABEL = { within: 'Within criterion', advisory: 'Advisory exceeded', none: 'Not assessed' };

export const outcomeLabel = outcome => OUTCOME_LABEL[outcome];

export const reportRows = record => [
    ['Assessment', record.id],
    ['Data origin', record.evidence.origin],
    ['Meter', record.evidence.meterId],
    ['Evidence time (UTC)', new Date(record.evidence.timestamp).toISOString()],
    ['Run time (UTC)', new Date(record.ranAt).toISOString()],
    ['Note', 'Snapshot comparison against user-configured operating criteria. Not a site compliance certificate or an executed equipment test.'],
    [],
    ['Check', 'Measured result', 'Operating criterion', 'Outcome'],
    ...record.checks.map(check => [check.name, check.measured, check.criterion, outcomeLabel(check.outcome)])
];
